import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { BacktestSpec } from '@thinkalpha/platform-ws-client/contracts/backtest.js';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { IfThenResearchPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import { renderGroup } from 'src/dtos/Idea/SearchPlanDTO';
import { IndicatorImportRefModel } from 'src/dtos/IndicatorImportRef';
import type { ConditionGroupModel } from 'src/features/searchAlpha/dtos/ConditionGroupModel';
import { conditionGroupModelFromIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelFromIfThenGroup';
import { conditionGroupModelToIfThenGroup } from 'src/features/searchAlpha/dtos/ConditionGroupModel/conditionGroupModelToIfThenGroup';
import type { SearchAlphaWidgetModel } from 'src/models/SearchAlphaWidgetModel';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Entry pane signal model. Wraps a SearchAlphaWidgetModel that owns the
 * actual condition tree; this model exposes derived signals (formula,
 * imports, conditions) and dirty/valid indicators based on the rendered
 * formula.
 */
export class EntryStepModel implements Disposable {
    readonly #searchModel = signal<SearchAlphaWidgetModel | null>(null);
    /** Bumped whenever the underlying search model fires a change event. */
    readonly #conditionsVersion = signal(0);
    readonly #lastSavedFormula = signal<string | null>(null);
    readonly #listenerBox = new DisposableBox();
    #pendingIdea: BacktestSpec['idea'] | null = null;

    readonly #logger: ReactBindings['Logger'];

    constructor(logger: ReactBindings['Logger']) {
        this.#logger = logger;
    }

    [Symbol.dispose](): void {
        this.#listenerBox[Symbol.dispose]();
    }

    get searchModel(): SearchAlphaWidgetModel | null {
        return this.#searchModel.value;
    }

    readonly conditions: ReadonlySignal<ConditionGroupModel[]> = computed(() => {
        void this.#conditionsVersion.value;
        return this.#searchModel.value?.children ?? [];
    });

    readonly formula: ReadonlySignal<string | null> = computed(() => {
        void this.#conditionsVersion.value;
        const searchModel = this.#searchModel.value;
        if (!searchModel) return null;
        try {
            const root = conditionGroupModelToIfThenGroup(searchModel.rootGroup);
            return renderGroup(root) ?? null;
        } catch {
            return null;
        }
    });

    readonly imports: ReadonlySignal<IndicatorImportRef[]> = computed(() => {
        void this.#conditionsVersion.value;
        const searchModel = this.#searchModel.value;
        if (!searchModel) return [];
        return searchModel.importsManager.imports.map((i) => i.toRef().toContractType());
    });

    readonly validationErrors: ReadonlySignal<string[]> = computed(() => {
        void this.#conditionsVersion.value;
        const searchModel = this.#searchModel.value;
        const errorCount = searchModel?.rootGroup.nestedChildStats.errorCount ?? 0;

        if (errorCount > 0) {
            return [
                errorCount === 1
                    ? 'Resolve the error in your entry conditions'
                    : `Resolve ${errorCount} errors in your entry conditions`,
            ];
        }

        const f = this.formula.value;
        if (f != null && f.length > 0) return [];
        return ['Define at least one entry condition'];
    });
    readonly isValid: ReadonlySignal<boolean> = computed(() => this.validationErrors.value.length === 0);

    readonly isDirty: ReadonlySignal<boolean> = computed(() => this.formula.value !== this.#lastSavedFormula.value);

    setSearchModel(model: SearchAlphaWidgetModel): void {
        this.#searchModel.value = model;

        const listener = (): void => {
            this.#conditionsVersion.value++;
        };
        model.rootGroup.changeTarget.addEventListener('search-change', listener);
        this.#listenerBox.next({
            [Symbol.dispose]: () => model.rootGroup.changeTarget.removeEventListener('search-change', listener),
        });
        this.#conditionsVersion.value++;

        if (this.#pendingIdea != null) {
            const idea = this.#pendingIdea;
            this.#pendingIdea = null;
            void this.hydrate(idea);
        }
    }

    /**
     * Hydrate entry conditions from an idea. If the search model isn't connected
     * yet, queues the idea until setSearchModel is called.
     */
    async hydrate(idea: BacktestSpec['idea']): Promise<void> {
        const searchModel = this.#searchModel.value;
        if (!searchModel) {
            this.#pendingIdea = idea;
            return;
        }

        // idea can be a UUID string or an inline object — only handle inline
        if (typeof idea === 'string' || idea == null) return;

        const plan = idea.plan;
        if (plan.type !== 'if-then') return;

        try {
            await this.#hydratePlan(plan);

            // The idea represents persisted state, so capture the post-hydration
            // formula as the saved baseline (form reads clean).
            this.markSaved();

            if (TRACE) this.#logger.trace('Entry conditions hydrated from idea');
        } catch (e) {
            this.#logger.error('Failed to hydrate entry conditions', {
                error: e instanceof Error ? e.message : String(e),
            });
        }
    }

    async applyQuickStart(plan: IfThenResearchPlan): Promise<void> {
        try {
            await this.#hydratePlan(plan);
            if (TRACE) this.#logger.trace('Entry conditions populated from quick-start preset');
        } catch (e) {
            this.#logger.error('Failed to apply quick-start preset', {
                error: e instanceof Error ? e.message : String(e),
            });
        }
    }

    async #hydratePlan(plan: IfThenResearchPlan): Promise<void> {
        const searchModel = this.#searchModel.value;
        if (!searchModel) return;

        searchModel.importsManager.clear();
        await searchModel.importsManager.addImportsFromRefs(
            plan.imports.map((ref) => IndicatorImportRefModel.fromIndicatorImportRef(ref)),
        );

        await searchModel.rootGroup.deferChanges(async () => {
            for (const child of [...searchModel.rootGroup.children]) {
                searchModel.rootGroup.removeChild(child);
            }
            conditionGroupModelFromIfThenGroup(plan.root, searchModel.rootGroup, true);
        });
    }

    markSaved(): void {
        this.#lastSavedFormula.value = this.formula.value;
    }

    reset(): void {
        this.#pendingIdea = null;
        const searchModel = this.#searchModel.value;
        if (searchModel) {
            searchModel.importsManager.clear();
            for (const child of [...searchModel.rootGroup.children]) {
                searchModel.rootGroup.removeChild(child);
            }
        }
        this.#lastSavedFormula.value = null;
    }
}
